import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, Avatar, tdColor, fmtOdds, bookName, VerdictChip, PosFilter, POS_GROUPS, posOf, inGameFilter } from '../ui'
import { getNFLValueTds } from '../api'
import { roleText, teamNick } from '../plain'
import { useOddsRange, inOddsRange } from '../oddsfilter'

// Value touchdown plays — players the model gives a 25-45% chance to score who face a defense that
// gives up TDs to their role. Not the most likely scorers: the ones whose price can be worth more
// than it looks (2026 wk1-6, plays shown at 25-45% hit 40.5% vs 35.8% shown). Each row shows the
// book's price next to our chance; ✓ VALUE = our chance beats the price. The list keeps its own
// "in testing" record, separate from the main record. Research only.
const pct = v => `${Math.round((v || 0) * 100)}%`

function Row({ r, onPlayer }) {
  const open = () => onPlayer && onPlayer({ gsis_id: r.gsis_id, full_name: r.name, pos: r.pos, team: r.team, opp: r.opp,
    headshot: r.headshot, market: 'Anytime TD', prop_type: 'anytime_td', status: 'active' })
  return (
    <button onClick={open} style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', cursor: 'pointer',
      background: T.card, border: `1px solid ${r.value ? T.greenMid : T.border}`, borderRadius: 12, padding: '11px 12px',
      marginBottom: 8, color: T.text }}>
      <Avatar url={r.headshot} name={r.name} team={r.team} size={40} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11.5, color: T.muted, fontWeight: 700 }}>{teamNick(r.team)} {roleText(r.role, r.role_label)} · vs {teamNick(r.opp)}</span>
          <VerdictChip label={`${r.grade.toUpperCase()} MATCHUP`} level={r.grade === 'Great' ? 'strong' : 'good'} />
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: T.muted2, marginTop: 4, lineHeight: 1.35 }}>
          {r.odds != null
            ? <>Book <b style={{ color: T.text, fontFamily: T.mono }}>{fmtOdds(r.odds)}</b>{r.book ? ` at ${bookName(r.book)}` : ''} ≈ {pct(r.implied)} ·{' '}
                {r.value ? <b style={{ color: T.green }}>our {pct(r.prob)} beats it by {r.edge_pp} pts</b>
                         : <span>our {pct(r.prob)} doesn't beat it</span>}</>
            : <>No book price yet — check back once lines post.</>}
        </span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0, minWidth: 52 }}>
        <span style={{ display: 'block', fontFamily: T.mono, fontSize: 21, fontWeight: 900, color: tdColor(r.prob) }}>{pct(r.prob)}</span>
        <span style={{ display: 'block', fontSize: 10, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>to score</span>
        {r.value && <span style={{ display: 'block', fontSize: 10, fontWeight: 900, color: T.green, marginTop: 3 }}>✓ VALUE</span>}
      </span>
    </button>
  )
}

export default function ValueTdsTab({ season = 2026, week = 1, onPlayer, gameFilter }) {
  const oddsRange = useOddsRange()
  const [d, setD] = useState(null)
  const [pos, setPos] = useState('ALL')
  useEffect(() => { let a = true; setD(null)
    getNFLValueTds(season, week).then(r => { if (a) setD(r || { rows: [] }) }); return () => { a = false } }, [season, week])
  if (!d) return <Spinner />
  const allRows = (d.rows || []).filter(r => inGameFilter(gameFilter, r.team) && inOddsRange(oddsRange, r.odds)), rec = d.record || {}
  const counts = Object.fromEntries(POS_GROUPS.map(g => [g, g === 'ALL' ? allRows.length : allRows.filter(r => posOf(r) === g).length]))
  const rows = pos === 'ALL' ? allRows : allRows.filter(r => posOf(r) === pos)
  const graded = (rec.wins || 0) + (rec.losses || 0)
  const nValue = rows.filter(r => r.value).length
  return (
    <div style={{ padding: '10px 14px 80px' }}>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.green}`, borderRadius: 11,
        padding: '11px 13px', marginBottom: 10, fontSize: 12.5, color: T.muted2, lineHeight: 1.5 }}>
        <b style={{ color: T.green }}>Value touchdown plays.</b> Not the likeliest scorers — players with a <b style={{ color: T.text }}>25–45%</b> chance
        who face a defense that keeps giving up TDs to their role. These usually come at long odds, so the price is the point:
        <b style={{ color: T.green }}> ✓ VALUE</b> means our chance beats what the book's price implies.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: T.card2,
        border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 12px', marginBottom: 12 }}>
        <span style={{ fontSize: 11.5, color: T.muted }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: T.amber, border: `1px solid ${T.amber}66`, borderRadius: 6, padding: '1px 6px', marginRight: 6, letterSpacing: 0.5 }}>IN TESTING</span>
          {graded ? <>This list: <b style={{ color: T.text, fontFamily: T.mono }}>{rec.wins}-{rec.losses}</b> ({rec.hit_rate}% scored vs {rec.avg_shown}% shown)
            {rec.roi != null && <> · <b style={{ color: rec.roi >= 0 ? T.green : T.red, fontFamily: T.mono }}>{rec.roi > 0 ? '+' : ''}{rec.roi}%</b> return at the flagged price</>}</>
            : <>Record starts this week — every pick is locked in when it first appears.</>}
        </span>
      </div>
      {allRows.length > 0 && <PosFilter value={pos} onChange={setPos} counts={counts} />}
      {rows.length === 0
        ? <Empty message={allRows.length ? `No ${pos}s on the list this week.` : 'No value TD spots yet — fills as projections and lines post.'} />
        : <>
            <div style={{ fontSize: 11, color: T.muted, margin: '0 2px 8px' }}>{rows.length} spots · {nValue} where our chance beats the price</div>
            {rows.map(r => <Row key={r.gsis_id} r={r} onPlayer={onPlayer} />)}
          </>}
      <div style={{ fontSize: 10, color: T.faint, marginTop: 10, lineHeight: 1.5 }}>
        Matchup = TDs this defense allows to his role vs league average (Great = a top-8 soft spot). Book price includes the book's margin.
        Separate from the main record · research only, not betting advice · 21+.
      </div>
    </div>
  )
}
